/***
 *
 *   APP NAV
 *   Primary navigation used inside the main app component
 *
 *   PROPS
 *   type: fixed or popup (style of nav on mobile)
 *   items: array of objects containing label, link and icon (optional)
 *
 **********/

import { useContext, useEffect, useState, type MouseEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Logo, Icon, useNavigate } from 'components/lib';
import ExitDialog from 'components/exit-dialog/ExitDialog';
import { ProjectAddExitContext } from 'contexts/ProjectAddExitContext';
import './app.scss';

interface AppNavItem {
  label: string;
  link?: string;
  icon?: string;
  position?: string;
  action?: (event: MouseEvent<HTMLDivElement>) => void;
}

interface AppNavProps {
  type?: 'fixed' | 'popup';
  items?: AppNavItem[];
}

export function AppNav(props: AppNavProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { requestProjectAddExit } = useContext(ProjectAddExitContext);
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [confirmedNavigateTo, setConfirmedNavigateTo] = useState<string | null>(null);

  // state
  const [open, _setOpen] = useState(false); // mobile is open
  const flowStep = new URLSearchParams(location.search).get('step');
  const isProjectCreationFlow = location.pathname.includes('/projects/add');
  const isProjectCreationProjectStep = isProjectCreationFlow && (!flowStep || flowStep === 'project');
  const isProjectCreationWizardStep = isProjectCreationFlow && flowStep != null && flowStep !== 'project';

  useEffect(() => {
    if (confirmedNavigateTo && location.pathname === confirmedNavigateTo) {
      setConfirmedNavigateTo(null);
    }
  }, [confirmedNavigateTo, location.pathname]);

  if (confirmedNavigateTo && location.pathname !== confirmedNavigateTo) {
    return <Navigate to={confirmedNavigateTo} replace />;
  }

  return (
    // <nav
    //   className={`sidebar popup ${open && 'open'} ${expanded && 'expanded'}`}
    //   onMouseEnter={(e) => setExpanded(true)}
    //   onMouseLeave={(e) => setExpanded(false)}
    // >
    // <nav className={`sidebar popup expanded`}>
    <>
      <nav className="sidebar popup">
        <Logo mark />
        <section className="nav-links">
          {props.items?.map((item) => {
            const itemClassName = item.position === 'bottom' ? 'bottom-item' : '';
            const testId =
              item.link === '/account'
                ? 'guided-tour-account-nav'
                : item.label === 'Guide'
                  ? 'guided-tour-guide-nav'
                  : item.label === 'Support'
                    ? 'guided-tour-support-nav'
                    : undefined;

            if (item.link) {
              const link = item.link;
              return (
                <div
                  key={item.label}
                  className={itemClassName}
                  data-guided-tour={testId}
                  style={{ width: 100 / (props.items?.length ?? 1) + '%' }}
                  onClick={() => {
                    if (isProjectCreationProjectStep) {
                      requestProjectAddExit(link);
                      return;
                    }
                    if (
                      isProjectCreationWizardStep ||
                      location.pathname.includes('/grids/add') ||
                      location.pathname.includes('/geodatas/add') ||
                      location.pathname.includes('/simulations/add')
                    ) {
                      setNavigateTo(link);
                      setExitModalOpen(true);
                    } else {
                      navigate(link);
                    }
                  }}
                >
                  {item.icon && <Icon className="icon" image={item.icon} size={18} color={open ? 'dark' : 'light'} />}
                  {item.label && <span className="label">{item.label}</span>}
                </div>
              );
            }

            return (
              <div
                key={item.label}
                className={itemClassName}
                data-guided-tour={testId}
                onClick={(event) => item.action?.(event)}
              >
                {item.icon && <Icon className="icon" image={item.icon} size={18} color={open ? 'dark' : 'light'} />}
                {item.label && <span className="label">{item.label}</span>}
              </div>
            );
          })}
        </section>
      </nav>
      <ExitDialog
        open={exitModalOpen}
        onClose={() => setExitModalOpen(false)}
        mode="confirm"
        onCancel={() => {
          setExitModalOpen(false);
        }}
        onConfirm={() => {
          setExitModalOpen(false);
          if (navigateTo) {
            setConfirmedNavigateTo(navigateTo);
          }
        }}
      />
    </>
  );
}
